import type { ComponentDocMap } from './types'

/**
 * Alias catalog — covers built-in aliases and documented context-specific
 * runtime aliases. Built-in entries must stay in sync with aliases.ts and
 * getAllAliases(); runtime entries must state which view provides them.
 */
export const aliasDocs: ComponentDocMap = {
  $start_time: {
    description: 'Default dataset start time: 168 hours before now (7 days ago).',
    type: 'ISODateTime',
  },
  $end_time: {
    description: 'Default dataset end time: 168 hours from now (7 days ahead).',
    type: 'ISODateTime',
  },
  $offset_start_time: {
    description: 'Default DSI start time offset in hours. Default: 0.',
    type: 'number',
  },
  $offset_end_time: {
    description: 'Default DSI end time offset in hours. Default: 0.',
    type: 'number',
  },
  $system_id: {
    description: 'Numeric ID of the active system.',
    type: 'number',
  },
  $system: {
    description: 'Name of the active system (e.g. "demostaden").',
    type: 'string',
  },
  $organization: {
    description: 'Organization name of the active system.',
    type: 'string',
  },
  $opt_model_id: {
    description: 'Primary optimization model ID for the active system.',
    type: 'number',
  },
  $currency: {
    description: 'Currency code of the active system in lowercase (e.g. "sek", "eur").',
    type: 'string',
  },
  $opt_time: {
    description: 'Timestamp of the optimization run to read from. Default: null (latest).',
    type: 'ISODateTime | null',
  },
  $opt_job_type_id: {
    description: 'Numeric optimization job type ID. Default: 2 (regular). See OPTJOB_TYPE_ID_TO_NAME in aliases.ts.',
    type: 'number',
    example: '4 (followup_without_deviations)',
  },
  $optjob_type: {
    description: 'String name of the optimization job type derived from $opt_job_type_id.',
    type: 'string',
    example: '"followup_without_deviations"',
  },
  $demand_heatload: {
    description: 'Source string for the demand heat load signal, used in optresults DSI sources.',
    type: 'string',
    example: '"demands.heat.heat"',
  },
  '$color-1': {
    description: 'Brand palette color 1 — teal (#22adbe).',
    type: 'hex string',
    example: '"$color-1"',
  },
  '$color-2': {
    description: 'Brand palette color 2 — blue (#1b72b8).',
    type: 'hex string',
    example: '"$color-2"',
  },
  '$color-3': {
    description: 'Brand palette color 3 — dark blue (#14476d).',
    type: 'hex string',
    example: '"$color-3"',
  },
  '$color-4': {
    description: 'Brand palette color 4 — navy (#2a3067).',
    type: 'hex string',
    example: '"$color-4"',
  },
  '$color-5': {
    description: 'Brand palette color 5 — dark green (#025933).',
    type: 'hex string',
    example: '"$color-5"',
  },
  '$color-6': {
    description: 'Brand palette color 6 — lime green (#92b023).',
    type: 'hex string',
    example: '"$color-6"',
  },
  '$color-7': {
    description: 'Brand palette color 7 — yellow (#C5BA00).',
    type: 'hex string',
    example: '"$color-7"',
  },
  '$color-8': {
    description: 'Brand palette color 8 — orange (#f07f13).',
    type: 'hex string',
    example: '"$color-8"',
  },
  '$color-9': {
    description: 'Brand palette color 9 — pink (#e72a80).',
    type: 'hex string',
    example: '"$color-9"',
  },
  '$color-10': {
    description: 'Brand palette color 10 — dark pink (#b22d76).',
    type: 'hex string',
    example: '"$color-10"',
  },
  '$color-11': {
    description: 'Brand palette color 11 — purple (#613178).',
    type: 'hex string',
    example: '"$color-11"',
  },
  '$color-12': {
    description: 'Brand palette color 12 — dark purple (#261e38).',
    type: 'hex string',
    example: '"$color-12"',
  },
  $light_red: {
    description: 'Semantic palette light red alias.',
    type: 'hex string',
  },
  $red: {
    description: 'Semantic palette red alias.',
    type: 'hex string',
  },
  $dark_red: {
    description: 'Semantic palette dark red alias.',
    type: 'hex string',
  },
  $light_green: {
    description: 'Semantic palette light green alias.',
    type: 'hex string',
  },
  $green: {
    description: 'Semantic palette green alias.',
    type: 'hex string',
  },
  $dark_green: {
    description: 'Semantic palette dark green alias.',
    type: 'hex string',
  },
  $light_yellow: {
    description: 'Semantic palette light yellow alias.',
    type: 'hex string',
  },
  $yellow: {
    description: 'Semantic palette yellow alias.',
    type: 'hex string',
  },
  $dark_yellow: {
    description: 'Semantic palette dark yellow alias.',
    type: 'hex string',
  },
  $light_blue: {
    description: 'Semantic palette light blue alias.',
    type: 'hex string',
  },
  $blue: {
    description: 'Semantic palette blue alias.',
    type: 'hex string',
  },
  $dark_blue: {
    description: 'Semantic palette dark blue alias.',
    type: 'hex string',
  },
  $light_pink: {
    description: 'Semantic palette light pink alias.',
    type: 'hex string',
  },
  $pink: {
    description: 'Semantic palette pink alias.',
    type: 'hex string',
  },
  $dark_pink: {
    description: 'Semantic palette dark pink alias.',
    type: 'hex string',
  },
  $light_grey: {
    description: 'Semantic palette light grey alias.',
    type: 'hex string',
  },
  $grey: {
    description: 'Semantic palette grey alias.',
    type: 'hex string',
  },
  $dark_grey: {
    description: 'Semantic palette dark grey alias.',
    type: 'hex string',
  },
  '$color-heatload': {
    description: 'Conventional color for heat demand/forecast series.',
    type: 'hex string',
  },
  '$color-pps': {
    description: 'Conventional color for PPS (potential production saving) series.',
    type: 'hex string',
  },
  '$color-odc': {
    description: 'Conventional color for ODC (operational deviation cost) series.',
    type: 'hex string',
  },
  '$color-4h-forecast': {
    description: 'Conventional color for 4-hour forecast series.',
    type: 'hex string',
  },
  '$color-24h-forecast': {
    description: 'Conventional color for 24-hour forecast series.',
    type: 'hex string',
  },
  '$color-48h-forecast': {
    description: 'Conventional color for 48-hour forecast series.',
    type: 'hex string',
  },
  '$color-168h-forecast': {
    description: 'Conventional color for 168-hour forecast series.',
    type: 'hex string',
  },
  '$color-2w-forecast': {
    description: 'Conventional color for 2-week forecast series.',
    type: 'hex string',
  },
  '$color-el': {
    description: 'Conventional color for electricity price series.',
    type: 'hex string',
  },
  '$color-temp': {
    description: 'Conventional color for temperature series.',
    type: 'hex string',
  },
  '$color-lmfu': {
    description: 'Conventional color for LMFU (lost margin from unavailability) series.',
    type: 'hex string',
  },
  '$color-rated-power': {
    description: 'Conventional color for rated power series.',
    type: 'hex string',
  },
  '$color-accumulator': {
    description: 'Conventional color for accumulator tank series.',
    type: 'hex string',
  },
  $availability_percentage: {
    description: 'Conventional availability color for percentage values.',
    type: 'hex string',
  },
  $availability_unavailability: {
    description: 'Conventional availability color for unavailable periods/segments.',
    type: 'hex string',
  },
  $availability_backing: {
    description: 'Conventional availability backing/background color.',
    type: 'hex string',
  },
  $availability_outcome: {
    description: 'Conventional availability color for outcome series.',
    type: 'hex string',
  },
  $bid_deadline_time: {
    description: 'The most recent past occurrence of the broker bid deadline time for the active system.',
    type: 'ISODateTime',
  },
  $el_bidding_area: {
    description: 'Electricity bidding area name for the active system (e.g. "SE3").',
    type: 'string',
  },
  $selected_partition_key: {
    description:
      'Stable key of the partition selected in a partition-aware Day Ahead scenario view. Use this for partition identity, not as the scenario optimization subtype.',
    type: 'string | null',
    example: '"north"',
  },
  $selected_partition_display_name: {
    description: 'Configuration-owned display label of the selected Day Ahead partition.',
    type: 'string | null',
    example: '"North"',
  },
  '$partition_<key>': {
    description:
      'Dynamic Day Ahead alias for each configured partition. The selected alias resolves to its partition key; inactive aliases resolve to "disabled_subtype". A customer config may use it as a regular-plan subtype only when that system deliberately uses matching identifiers. Configured keys must remain unique after alias normalization; collisions fail partitioned scenarios closed.',
    type: 'string',
    example: '$partition_north → "north"; $partition_south → "disabled_subtype"',
  },
  $selected_subtype: {
    description:
      'Concrete optimization subtype selected for the current result context. In Day Ahead this is resolved separately for each scenario; in Intraday it is the directly selected subtype.',
    type: 'string | null',
    example: '"scenario_1_north"',
  },
  $selected_subtype_display_name: {
    description: 'Display label for the currently selected subtype or its semantic partition.',
    type: 'string | null',
    example: '"North"',
  },
  '$subtype_<key>': {
    description:
      'Dynamic result alias for each configured selection key. The selected alias resolves to the concrete optimization subtype; inactive aliases resolve to "disabled_subtype". Configured keys must remain unique after alias normalization.',
    type: 'string',
    example: '$subtype_north → "scenario_1_north"; $subtype_south → "disabled_subtype"',
  },
  $elplan_partition_id: {
    description:
      'Numeric selected Day Ahead partition ID. Use for partition-aware Elplan bid datasets; do not use it as an optimization subtype.',
    type: 'number',
    example: '12',
  },
}
